import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Lock, Eye, EyeOff } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * Change Password — logged-in flow.
 *
 * Mobile polish (2026-05-23):
 *   - Reduced icon size (h-4 w-4) and tightened side padding so the
 *     placeholder text never clips on 320–412px viewports.
 *   - Inputs use pl-9 pr-9 instead of pl-10 pr-10 to give placeholder
 *     room without sacrificing icon touch targets.
 *   - Left lock icon uses pointer-events-none so it never steals taps
 *     from the input field.
 *   - Eye toggle uses a -translate-y-1/2 centering pattern so it stays
 *     visually centered regardless of the input height.
 *   - Convention: Eye = "currently visible" (matches Login.jsx). Toggle
 *     swaps `type="password" <-> type="text"` reliably for all three
 *     fields (current / new / confirm). aria-pressed + aria-label make
 *     state explicit for screen readers.
 */

const ChangePassword = () => {
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (newPassword !== confirmPassword) {
      toast.error('New passwords do not match');
      return;
    }

    if (newPassword.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }

    if (currentPassword === newPassword) {
      toast.error('New password must be different from current password');
      return;
    }

    setLoading(true);

    try {
      const response = await axios.post(
        `${API}/auth/change-password`,
        {
          current_password: currentPassword,
          new_password: newPassword,
        },
        { withCredentials: true },
      );

      if (response.data.success) {
        toast.success('Password changed successfully!');
        setCurrentPassword('');
        setNewPassword('');
        setConfirmPassword('');
      }
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Change password error:', error);
      }
      const errorMsg = error.response?.data?.detail || 'Failed to change password';
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  // Shared field shell: tight side padding so long placeholders never clip
  // on 320–412px Android viewports, icons properly centered via translate.
  const fieldClass = 'pl-9 pr-9 h-11';

  // Small, accessible toggle button used for all 3 fields. `aria-pressed`
  // makes the show/hide state explicit; `pointer-events-auto` is implicit
  // (button default), but the surrounding wrapper isn't pointer-blocked.
  const ToggleEye = ({ shown, onToggle, testid, fieldLabel }) => (
    <button
      type="button"
      onClick={onToggle}
      aria-label={shown ? `Hide ${fieldLabel}` : `Show ${fieldLabel}`}
      aria-pressed={shown}
      data-testid={testid}
      className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded text-gray-400 hover:text-gray-600 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-purple-300"
    >
      {shown ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}
    </button>
  );

  return (
    <Card className="border-2 border-purple-200">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Lock className="h-5 w-5 text-purple-600" />
          Change Password
        </CardTitle>
        <p className="text-sm text-gray-600">
          Update your password to keep your account secure
        </p>
      </CardHeader>

      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Current Password */}
          <div className="space-y-2">
            <Label htmlFor="current-password">Current Password</Label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
              <Input
                id="current-password"
                type={showCurrent ? 'text' : 'password'}
                placeholder="Current password"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                className={fieldClass}
                data-testid="change-password-current-input"
                required
              />
              <ToggleEye
                shown={showCurrent}
                onToggle={() => setShowCurrent((v) => !v)}
                testid="change-password-current-toggle"
                fieldLabel="current password"
              />
            </div>
          </div>

          {/* New Password */}
          <div className="space-y-2">
            <Label htmlFor="new-password">New Password</Label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
              <Input
                id="new-password"
                type={showNew ? 'text' : 'password'}
                placeholder="New password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                className={fieldClass}
                data-testid="change-password-new-input"
                required
              />
              <ToggleEye
                shown={showNew}
                onToggle={() => setShowNew((v) => !v)}
                testid="change-password-new-toggle"
                fieldLabel="new password"
              />
            </div>
            <p className="text-xs text-gray-500">
              At least 8 characters with uppercase, lowercase, and a number.
            </p>
          </div>

          {/* Confirm Password */}
          <div className="space-y-2">
            <Label htmlFor="confirm-password">Confirm New Password</Label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
              <Input
                id="confirm-password"
                type={showConfirm ? 'text' : 'password'}
                placeholder="Confirm password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className={fieldClass}
                data-testid="change-password-confirm-input"
                required
              />
              <ToggleEye
                shown={showConfirm}
                onToggle={() => setShowConfirm((v) => !v)}
                testid="change-password-confirm-toggle"
                fieldLabel="confirm password"
              />
            </div>
          </div>

          <Button
            type="submit"
            disabled={loading}
            data-testid="change-password-submit"
            className="w-full bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700"
          >
            {loading ? 'Changing Password...' : 'Change Password'}
          </Button>
        </form>
      </CardContent>
    </Card>
  );
};

export default ChangePassword;
